/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useHistory, useRouteMatch} from 'react-router'
import {
    Box,
    Heading,
    Text,
    Stack,
    Button,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useOrder} from '@salesforce/commerce-sdk-react'
import Link from '@salesforce/retail-react-app/app/components/link'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {
    backLinkStyle,
    orderItemContainerStyle,
    orderProductContainerStyle,
    pageContainerStyle,
    pageHeadingStyle
} from './order-detail.style'
import {pxToRem} from '../../../../utills/pxToRem'
import {OrderProducts} from '../../../../components/orders/products'
import {OrderDetail} from '../../../../components/orders/detail'
import {OrderInfo} from '../../../../components/orders/info'
import {transformGiftCertificateItems} from '../../../../utills/transformBasket'
import {useToast} from '../../../../hooks/use-toast'

const onClient = typeof window !== 'undefined'

const AccountOrderDetail = () => {
    const headingRef = useRef()
    const {params} = useRouteMatch()
    const showToast = useToast()
    const history = useHistory()
    const {formatMessage} = useIntl()
    const [isBack, setIsBack] = useState(false)

    const {
        data: order,
        isLoading: isOrderLoading,
        error: orderError
    } = useOrder(
        {
            parameters: {orderNo: params.orderNo}
        },
        {
            enabled: onClient && !!params.orderNo
        }
    )
    const isLoading = isOrderLoading || !order
    const giftCertificateItems = transformGiftCertificateItems(order?.giftCertificateItems)
    const giftCertificateItemsCount = giftCertificateItems?.length || 0
    const itemCount =
        (order?.productItems?.reduce((count, {quantity}) => count + quantity, 0) || 0) +
        giftCertificateItemsCount

    const handleBackClick = (e) => {
        if (history.action === 'PUSH') {
            e.preventDefault()
            setIsBack(true)
            history.goBack()
        }
    }

    useEffect(() => {
        // Focus the 'Order Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    useLayoutEffect(() => {
        const nav =
            document.querySelector('#account-detail-nav a:nth-child(2)') ||
            document.querySelector('#account-detail-nav li:nth-child(2) a')
        if (nav) {
            nav.classList.add('active')
            nav.setAttribute('aria-current', 'page')
        }
    })

    useEffect(() => {
        return () => {
            const nav =
                document.querySelector('#account-detail-nav a:nth-child(2)') ||
                document.querySelector('#account-detail-nav li:nth-child(2) a')
            if (!isBack && nav) {
                nav.classList.remove('active')
                nav.removeAttribute('aria-current')
            }
        }
    }, [isBack])

    if (orderError && !isLoading) {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
        return null
    }

    return (
        <Stack {...pageContainerStyle} data-testid="account-order-details-page">
            <Stack>
                <Box>
                    <Button
                        {...backLinkStyle}
                        as={Link}
                        to={'/account/orders'}
                        variant="link"
                        onClick={handleBackClick}
                    >
                        <FormattedMessage
                            defaultMessage="<< Back to all orders"
                            id="account_order_detail.link.back_to_all_orders"
                        />
                    </Button>
                </Box>

                <Stack mt={{base: pxToRem(15), lg: pxToRem(20)}}>
                    <Heading as="h1" tabIndex="0" ref={headingRef} {...pageHeadingStyle}>
                        <FormattedMessage
                            defaultMessage="Order Details"
                            id="account_order_detail.title.order_details"
                        />
                    </Heading>

                    <OrderInfo isLoading={isLoading} order={order} />
                </Stack>
            </Stack>
            <OrderDetail isLoading={isLoading} order={order} />
            <Stack spacing={4} sx={orderItemContainerStyle}>
                {!isLoading && (
                    <Text>
                        {formatMessage(
                            {
                                defaultMessage:
                                    '{itemCount} {itemCount, plural, one {Item} other {Items}}',
                                id: 'account_order_detail.heading.num_of_items_dynamic'
                            },
                            {itemCount}
                        )}
                    </Text>
                )}
                <Stack sx={orderProductContainerStyle} divider={<Divider />}>
                    <OrderProducts
                        isLoading={isLoading}
                        productItems={order?.productItems}
                        giftCertificateItems={giftCertificateItems}
                        currency={order?.currency}
                    />
                </Stack>
            </Stack>
        </Stack>
    )
}

AccountOrderDetail.getTemplateName = () => 'account-order-history'

export default AccountOrderDetail
